import React, { useState } from "react";
import i18next from "../../assets/i18n/i18n";
import "./select-office.scss";
import { Box, Button, FormControl, FormControlLabel, RadioGroup, Radio, Grid } from "@material-ui/core";
import ManDrawingOnBoard from "../../assets/images/png/ManDrawingOnBoard.png";
import { Redirect } from "react-router-dom";
import config from '../../config';
import SelectedIcon from "../../assets/images/png/selectedIcon.png";
import UnSelectedIcon from "../../assets/images/png/notSelectedIcon.png";
import AuthenticationApi from "../../services/authentication-api";
import { InformationPageType, redirectToInfomationPage } from "pages/Information";
import Spinner from "components/Spinner";

type SelectOfficeProps = {
    location: {
        state: {
            oso: string[][];
            acs: string;
            spFull: string;
            sessionId: string;
            pi2AuthToken: string;
            authType: string;
        };
    };
};

interface Oso {
    office: string;
    subOffice: string;
    user: string;
    name: string;
    firstName: string;
}

const mapUser = (array: string[]): Oso => {
    const [office, subOffice, user, name, firstName] = array;
    return {
        office,
        subOffice,
        user,
        name,
        firstName,
    };
};

const SelectOffice = ({ location }: SelectOfficeProps): React.ReactElement => {
    const { oso, acs, spFull, sessionId, pi2AuthToken, authType } = location.state;
    const [redirectingToInformation, setRedirectingToInformation] = useState<InformationPageType | undefined>(
        undefined,
    );

    const users = oso.map((office) => mapUser(office));
    const username = users.length >= 1 ? `${users[0].name} ${users[0].firstName}` : "";

    const [state, setState] = useState({
        selectedUserIndex: -1,
        username,
        redirectToLoginPage: false,
        token: "",
        isLoading: false,
    });
    const isSelectedOffice = (office: string, subOffice: string): boolean => {
        if (state.selectedUserIndex === -1) return false;

        const selectedOffice = users[state.selectedUserIndex];

        return selectedOffice.office === office && selectedOffice.subOffice === subOffice;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        event.preventDefault();
        setState({
            ...state,
            selectedUserIndex: parseInt(event.target.value, 10),
        });
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        const authenticationApi: AuthenticationApi = new AuthenticationApi();

        const account = users.find((u) => isSelectedOffice(u.office, u.subOffice));

        if (account === undefined) {
            console.error("Account not found");
            return;
        }
        setState({ ...state, isLoading: true });

        authenticationApi
            .selectOfficeSubOffice({
                office: account.office,
                subOffice: account.subOffice,
                acs,
                issuer: spFull,
                pi2AuthToken,
                sessionId,
            })
            .then((response) => {
                if (response.status === "success") {
                    authenticationApi.autoPost({
                        acs,
                        sp: spFull,
                        token: response.artifact,
                    });
                } else {
                    setRedirectingToInformation(InformationPageType.GENERIC_ERROR);
                    console.error("An error occured during the selection of offices");
                }
            })
            .catch((error) => {
                console.error(error);
                setRedirectingToInformation(InformationPageType.GENERIC_ERROR);
            });
    };

    const handleCancel = (): void => {
        setState({
            ...state,
            redirectToLoginPage: true,
        });
    };

    if (redirectingToInformation !== undefined) {
        return redirectToInfomationPage(redirectingToInformation);
    }

    if (state.redirectToLoginPage) {
        return (
            <Redirect
                to={{
                    pathname: `/login`,
                    search: `?${config.PI2_SP}=${spFull}&${config.PI2_AUTH_TYPE}=${authType ?? ";EXT[oAuth/itsme];"}&${
                        config.PI2_ACS
                    }=${acs}`,
                }}
            />
        );
    }

    return (
        <Box>
            <Spinner isLoading={state.isLoading} />
            <Grid container style={{ height: "555px" }}>
                <Grid item xs={1} />
                <Grid item xs={6}>
                    <Grid container className="welcome-user-container">
                        <Grid item xs={12} className="welcome-user-header">
                            {i18next.t("SelectOffice.WelcomeUser.Header")} {username}
                        </Grid>
                        <Grid item xs={4} className="welcome-user-info-text">
                            {i18next.t("SelectOffice.WelcomeUser.InfoText")}
                        </Grid>
                        <Grid item xs={8} />
                    </Grid>
                </Grid>
                <Grid item xs={5}>
                    <Grid container className="select-office-container">
                        <Grid item xs={1} />
                        <Grid item xs>
                            <Grid container item xs>
                                <Grid item xs={1} />
                                <Grid item xs>
                                    <form className="select-office-form" onSubmit={handleSubmit}>
                                        <Grid className="select-office-list">
                                            <RadioGroup name="offices">
                                                {users.map((user, index) => {
                                                    const isSelected = isSelectedOffice(user.office, user.subOffice);
                                                    const icon = isSelected ? SelectedIcon : UnSelectedIcon;
                                                    const marginSize = isSelected ? "0.75em" : "1em";
                                                    return (
                                                        <FormControlLabel
                                                            key={`${user.office}-${user.subOffice}`}
                                                            value={user.office}
                                                            control={
                                                                <Radio
                                                                    className="select-office-form-radio"
                                                                    onChange={handleChange}
                                                                    checked={isSelected}
                                                                    name={user.office}
                                                                    value={index}
                                                                />
                                                            }
                                                            label={
                                                                <div>
                                                                    <p style={{ fontFamily: "Nexa" }}>
                                                                        <img
                                                                            src={icon}
                                                                            alt="icon"
                                                                            style={{ marginRight: marginSize }}
                                                                        />
                                                                        {`${user.office} - ${
                                                                            user.user.split("[")[0]
                                                                        } (${user.subOffice})`}
                                                                    </p>
                                                                </div>
                                                            }
                                                            id={`select-office-radio-${user.office}`}
                                                            className="select-office-form-radio-label"
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <div className="btn-form-group">
                                            <Button
                                                className="btn primary-btn select-office-form select-office-form-btn select-office-form-btn-submit"
                                                id="select-office-btn-submit"
                                                type="submit"
                                                disabled={state.selectedUserIndex === -1}
                                            >
                                                {i18next.t("Buttons.Select")}
                                            </Button>
                                            <Button
                                                className="btn btn-cancel select-office-form select-office-form-btn"
                                                onClick={handleCancel}
                                                id="select-office-btn-cancel"
                                            >
                                                {i18next.t("Buttons.Cancel")}
                                            </Button>
                                        </div>
                                    </form>
                                    <FormControl
                                        className="select-office-form"
                                        fullWidth
                                        margin="normal"
                                        variant="filled"
                                    />
                                </Grid>
                                <Grid item xs={1} />
                            </Grid>
                        </Grid>
                        <Grid item xs={1} />
                    </Grid>
                </Grid>
                <img src={ManDrawingOnBoard} alt="Man drawing on board" className="img-man-drawing" />
                <div
                    style={{
                        borderTop: "1px solid #e8ebeb",
                        width: "100%",
                        position: "absolute",
                        top: "39em",
                    }}
                />
            </Grid>
        </Box>
    );
};

export default SelectOffice;
